import { queryOptions } from '@tanstack/react-query'
import type { Database } from '@/types/supabase'
import { countItemStats } from '@/lib/bulk-jobs/status'
import type { BulkJobStatus } from '@/lib/bulk-jobs/types'
import { supabase } from '@/lib/supabase/client'

// Re-export shared status type for backwards compatibility
export type { BulkJobStatus as StpBulkJobStatus }

// Re-export shared deriveJobStatus for consumers
export { deriveJobStatus } from '@/lib/bulk-jobs/status'

// =============================================================================
// STP Bulk Job Types
// =============================================================================

type StpBulkJobRow = Database['public']['Tables']['stp_bulk_jobs']['Row']

/**
 * STP Bulk job with author profile and progress counts.
 * Status is derived from items, not the DB status column.
 */
export type StpBulkJob = StpBulkJobRow & {
  profiles: {
    id: string
    email: string
    full_name: string | null
    avatar_url: string | null
  } | null
  /** Number of items that are done (completed or failed) - derived from items */
  processed_items: number
  /** Number of items that failed or errored */
  failed_items: number
}

/**
 * Active bulk job with progress information for the indicator
 */
export type StpBulkJobWithProgress = {
  id: string
  name: string
  status: BulkJobStatus
  total_items: number
  created_at: string
  /** Number of items that are done (completed or failed) */
  processed_items: number
  /** Progress percentage (0-100) */
  progress_percent: number
}

// =============================================================================
// Helpers
// =============================================================================

type StpBulkJobItemRaw = {
  error_message: string | null
  product_id: string | null
  products: {
    generations: {
      status: string
    } | null
  } | null
}

function toStatusItem(item: StpBulkJobItemRaw) {
  return {
    error_message: item.error_message,
    result_entity_id: item.product_id,
    generation_status: item.products?.generations?.status ?? null,
  }
}

// =============================================================================
// STP Bulk Job Queries
// =============================================================================

export const stpBulkJob = {
  findOne: (jobId: string) =>
    queryOptions({
      queryKey: ['stp-bulk-job', jobId],
      queryFn: async (): Promise<StpBulkJob | null> => {
        if (!jobId || jobId.trim() === '') {
          return null
        }

        const { data, error } = await supabase
          .from('stp_bulk_jobs')
          .select(
            `
            *,
            profiles!author(id, email, full_name, avatar_url),
            stp_bulk_job_items!bulk_job_id(
              error_message,
              product_id,
              products!product_id(
                generations!current_generation_id(status)
              )
            )
          `
          )
          .eq('id', jobId)
          .maybeSingle()

        if (error) {
          throw new Error(`Failed to fetch STP bulk job: ${error.message}`, {
            cause: error,
          })
        }

        if (!data) {
          return null
        }

        const stats = countItemStats(
          data.stp_bulk_job_items || [],
          toStatusItem
        )
        return {
          ...data,
          processed_items: stats.processed,
          failed_items: stats.failed,
          stp_bulk_job_items: undefined,
        } as unknown as StpBulkJob
      },
      staleTime: 30 * 1000,
    }),

  findManyByWorkspace: (workspaceId: string) =>
    queryOptions({
      queryKey: ['stp-bulk-jobs', 'workspace', workspaceId],
      queryFn: async ({ signal }): Promise<StpBulkJob[]> => {
        if (!workspaceId || workspaceId.trim() === '') {
          throw new Error(
            'Invalid workspaceId: cannot fetch STP bulk jobs without valid workspace ID'
          )
        }

        const { data, error } = await supabase
          .from('stp_bulk_jobs')
          .select(
            `
            *,
            profiles!author(id, email, full_name, avatar_url),
            stp_bulk_job_items!bulk_job_id(
              error_message,
              product_id,
              products!product_id(
                generations!current_generation_id(status)
              )
            )
          `
          )
          .eq('workspace_id', workspaceId)
          .order('created_at', { ascending: false })
          .abortSignal(signal)

        if (error) {
          throw new Error(`Failed to fetch STP bulk jobs: ${error.message}`, {
            cause: error,
          })
        }

        return (data || []).map((job) => {
          const stats = countItemStats(
            job.stp_bulk_job_items || [],
            toStatusItem
          )
          return {
            ...job,
            processed_items: stats.processed,
            failed_items: stats.failed,
            stp_bulk_job_items: undefined,
          }
        }) as unknown as StpBulkJob[]
      },
      staleTime: 30 * 1000,
    }),

  /**
   * Get active bulk jobs with progress info for the indicator.
   * A job is "active" if any item's generation is still pending/processing.
   * Status is derived from items, not the stored status column.
   */
  findManyActiveWithProgress: (workspaceId: string) =>
    queryOptions({
      queryKey: ['stp-bulk-jobs', 'workspace', workspaceId, 'active'],
      queryFn: async ({ signal }): Promise<StpBulkJobWithProgress[]> => {
        if (!workspaceId || workspaceId.trim() === '') {
          return []
        }

        // Get recent jobs with their items and generation status
        const { data: jobs, error: jobsError } = await supabase
          .from('stp_bulk_jobs')
          .select(
            `
            id,
            name,
            total_items,
            created_at,
            stp_bulk_job_items!bulk_job_id(
              id,
              product_id,
              error_message,
              products!product_id(
                current_generation_id,
                generations!current_generation_id(status)
              )
            )
          `
          )
          .eq('workspace_id', workspaceId)
          .order('created_at', { ascending: false })
          .limit(20) // Only check recent jobs for performance
          .abortSignal(signal)

        if (jobsError) {
          throw new Error(
            `Failed to fetch active STP bulk jobs: ${jobsError.message}`,
            { cause: jobsError }
          )
        }

        if (!jobs || jobs.length === 0) {
          return []
        }

        // Calculate progress and filter to only active jobs
        return jobs
          .map((job) => {
            const stats = countItemStats(
              job.stp_bulk_job_items || [],
              toStatusItem
            )
            return { job, processedCount: stats.processed }
          })
          .filter(({ job, processedCount }) => processedCount < job.total_items)
          .map(({ job, processedCount }) => ({
            id: job.id,
            name: job.name,
            status: 'processing' as const,
            total_items: job.total_items,
            created_at: job.created_at,
            processed_items: processedCount,
            progress_percent:
              job.total_items > 0
                ? Math.round((processedCount / job.total_items) * 100)
                : 0,
          }))
      },
      // Short stale time for real-time feel
      staleTime: 5 * 1000,
      // Refetch more frequently when there are active jobs
      refetchInterval: (query) => {
        const data = query.state.data
        // If there are active jobs, poll every 5 seconds as backup to subscriptions
        return data && data.length > 0 ? 5000 : false
      },
    }),
}
